import { signal, computed } from "@preact/signals";

// ── Estado global (signals) ────────────────────────────────────────────────
// messages: [{ key, sender: 'user'|'bot', text, buttons?, list?, status?, ts }]
export const messages = signal([]);

// conversation: metadatos de la conversación activa
export const conversation = signal({
  id: null,
  state: null,
  isVerified: false,
  clientId: null,
  clientName: null,
  identificationRequired: false,
  handoffActive: false
});

// ── Scroll infinito del historial ──────────────────────────────────────────
// Cursor de la próxima página hacia atrás ({ before, beforeId }), tal como lo
// devuelve el server. null = no hay más historial que traer.
export const historyCursor = signal(null);
export const loadingOlder = signal(false);
// "conversation" = sólo esta sesión (identidad sin validar);
// "customer" = el hilo del abonado, también lo hablado en otros dispositivos.
export const historyScope = signal("conversation");
export const hasMoreHistory = computed(() => historyCursor.value !== null);

export const agentName = signal(null); // nombre del agente humano en handoff
export const phase = signal("booting"); // booting | ready | error

// Envíos de texto en vuelo. El webchat deja mandar varios mensajes seguidos sin
// esperar la respuesta: el bridge los agrupa en un solo procesamiento (buffer de
// debounce por usuario, igual que en WhatsApp). Por eso "enviando" es un
// contador y no un booleano — tiene que seguir en true hasta que vuelva el
// último de los envíos, no el primero.
export const inFlightSends = signal(0);
export const sending = computed(() => inFlightSends.value > 0);
export const uploading = signal(false);
export const online = signal(typeof navigator !== "undefined" ? navigator.onLine : true);

if (typeof window !== "undefined") {
  window.addEventListener("online", () => { online.value = true; });
  window.addEventListener("offline", () => { online.value = false; });
}

// El último mensaje del bot es el único que muestra quick replies activos.
export const lastBotMessage = computed(() => {
  const list = messages.value;
  for (let i = list.length - 1; i >= 0; i--) {
    if (list[i].sender === "bot") return list[i];
  }
  return null;
});

/**
 * Aplica al estado de la conversación lo que devolvió el server.
 *
 * `state` se toma por presencia, no por truthiness: el server lo manda en null
 * cuando el bot ya no espera nada (identificación terminada), y ese null tiene
 * que pisar el valor anterior. Con `??` quedaba el "waiting_id" viejo y el
 * composer seguía en modo numérico hasta recargar la página.
 *
 * El resto de los campos sí usa `??`: son datos del cliente que el payload
 * omite cuando no cambian.
 */
export function mergeConversationPayload(prev, payload = {}) {
  // Volver a pedir identificación ("Cambiar cuenta") cierra la sesión de la
  // cuenta anterior: ahí el null del payload sí es un valor, o el header
  // seguiría mostrando el abonado que ya no está.
  const accountCleared = payload.identificationRequired === true;
  return {
    ...prev,
    state: payload.state === undefined ? prev.state : payload.state,
    isVerified: payload.isVerified ?? prev.isVerified,
    clientId: accountCleared ? (payload.clientId ?? null) : (payload.clientId ?? prev.clientId),
    clientName: accountCleared ? (payload.clientName ?? null) : (payload.clientName ?? prev.clientName),
    identificationRequired: payload.identificationRequired ?? prev.identificationRequired,
    handoffActive: payload.handoffActive ?? prev.handoffActive
  };
}

let keyCounter = 0;
export function nextKey() {
  keyCounter += 1;
  return `m${Date.now().toString(36)}-${keyCounter}`;
}

export function appendMessage(message) {
  messages.value = [...messages.value, message];
  return message.key;
}

export function updateMessage(key, patch) {
  messages.value = messages.value.map((m) => (m.key === key ? { ...m, ...patch } : m));
}
